import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Typography,
  Chip,
  Box
} from '@mui/material';
import { LocationOn, Phone, Language } from '@mui/icons-material';
import { put } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Location } from '../types';

interface LocationsListProps {
  locations: Location[];
  onLocationUpdate: () => void;
}

function LocationsList({ locations, onLocationUpdate }: LocationsListProps) {

  const handleToggleAutoReply = async (locationId: string, enabled: boolean) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      await put({
        apiName: 'reviewReplyApi',
        path: `/locations/${locationId}/settings`,
        options: {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: { autoReplyEnabled: enabled }
        }
      }).response;
      
      // Update local state
      if (onLocationUpdate) {
        onLocationUpdate();
      }
      
      console.log(`Auto-reply ${enabled ? 'enabled' : 'disabled'} for location ${locationId}`);
    } catch (error) {
      console.error('Failed to update auto-reply setting:', error);
    }
  };

  if (!locations || locations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No locations found. Make sure your Google Business Profile has locations set up.
      </Typography>
    );
  }

  return (
    <List>
      {locations.map((location) => (
        <ListItem key={location.LocationId} divider>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn color="primary" />
                <Typography variant="h6">{location.Title}</Typography>
                <Chip 
                  label={location.AutoReplyEnabled ? 'Auto-Reply ON' : 'Auto-Reply OFF'} 
                  color={location.AutoReplyEnabled ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            }
            secondary={
              <>
                {location.Address && (
                  <span style={{ display: 'block', marginTop: '8px' }}>
                    {location.Address}
                  </span>
                )}
                {location.PhoneNumbers && location.PhoneNumbers.length > 0 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                    <Phone fontSize="small" />
                    {location.PhoneNumbers[0]}
                  </span>
                )}
                {location.WebsiteUri && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                    <Language fontSize="small" />
                    {location.WebsiteUri}
                  </span>
                )}
              </>
            }
          />
          <ListItemSecondaryAction>
            <Switch
              checked={location.AutoReplyEnabled || false}
              onChange={(e) => handleToggleAutoReply(location.LocationId, e.target.checked)}
            />
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </List>
  );
}

export default LocationsList;
